import React, { useState } from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Lang, SUPPORTED_LANGS } from '.';
import { useTranslation } from './I18nProvider';

interface FlagProps {
  size?: number;
}

function FlagBR({ size = 20 }: FlagProps) {
  return (
    <svg width={size} height={size * (14 / 20)} viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="20" height="14" rx="2" fill="#009C3B" />
      <polygon points="10,2 18,7 10,12 2,7" fill="#FFDF00" />
      <circle cx="10" cy="7" r="2.7" fill="#002776" />
      <path d="M7.6 7.4 Q10 6.2 12.4 7.4" stroke="#fff" strokeWidth="0.5" fill="none" />
    </svg>
  );
}

function FlagUS({ size = 20 }: FlagProps) {
  // Simplified US flag: 7 stripes + blue canton.
  return (
    <svg width={size} height={size * (14 / 20)} viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="20" height="14" rx="2" fill="#B22234" />
      {[1, 3, 5, 7, 9, 11].map((y) => (
        <rect key={y} x="0" y={y} width="20" height="1" fill="#fff" />
      ))}
      <rect width="9" height="7" fill="#3C3B6E" />
      {/* a few decorative stars */}
      {[
        [1.5, 1.5],
        [4.5, 1.5],
        [7.5, 1.5],
        [3, 3],
        [6, 3],
        [1.5, 4.5],
        [4.5, 4.5],
        [7.5, 4.5],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="0.5" fill="#fff" />
      ))}
    </svg>
  );
}

function FlagES({ size = 20 }: FlagProps) {
  return (
    <svg width={size} height={size * (14 / 20)} viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="20" height="14" rx="2" fill="#AA151B" />
      <rect y="3.5" width="20" height="7" fill="#F1BF00" />
      <rect x="3.5" y="5" width="1.5" height="4" fill="#AA151B" rx="0.2" />
    </svg>
  );
}

const FLAGS: Record<Lang, React.ComponentType<FlagProps>> = {
  pt: FlagBR,
  en: FlagUS,
  es: FlagES,
};

interface LanguageSwitcherProps {
  /** When true, render only the flag without a chevron — for compact mobile mode. */
  compact?: boolean;
  /** Color of the surrounding pill (defaults to subtle gray). */
  variant?: 'light' | 'dark';
}

export function LanguageSwitcher({ compact = false, variant = 'light' }: LanguageSwitcherProps) {
  const { lang, setLang, t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const CurrentFlag = FLAGS[lang];

  const open = Boolean(anchor);

  function handleSelect(next: Lang) {
    setLang(next);
    setAnchor(null);
  }

  const pillBg = variant === 'dark' ? 'rgba(255,255,255,0.12)' : '#0000000A';
  const pillHoverBg = variant === 'dark' ? 'rgba(255,255,255,0.20)' : '#00000014';
  const chevronColor = variant === 'dark' ? '#fff' : '#6C6C6C';

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={t.languageSwitcher.label}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          background: pillBg,
          border: 'none',
          borderRadius: '999px',
          cursor: 'pointer',
          px: compact ? 1 : 1.25,
          py: 0.5,
          minHeight: 36,
          transition: 'background-color 0.15s ease',
          '&:hover': { background: pillHoverBg },
        }}
      >
        <CurrentFlag size={22} />
        {!compact && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
            <path d="M2 4l3 3 3-3" stroke={chevronColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </Box>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', minWidth: 160 } } }}
      >
        {SUPPORTED_LANGS.map((l) => {
          const Flag = FLAGS[l];
          const label = t.languageSwitcher[l];
          return (
            <MenuItem
              key={l}
              selected={l === lang}
              onClick={() => handleSelect(l)}
              sx={{ gap: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Flag size={22} />
              </ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
